import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RoundedIconButton } from "@/components/ui/rounded-icon-button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { FilterIcon, X } from "lucide-react";
import { StatusFilter } from "./filters/StatusFilter";
import { SortByFilter } from "./filters/SortByFilter";
import { SortOrderFilter } from "./filters/SortOrderFilter";
import { FilterActions } from "./filters/FilterActions";
import { DateFilter } from "./filters/DateFilter";
import { TodayFilterPanelOption } from "./TodayFilterPanelOption";
import { getActiveFilterCount } from "../utils/getActiveFilterCount";
import {
  applyDateChange,
  applyTodayFilterChange,
} from "../utils/applyVisitFilterChanges";

export interface ContractorPanelVisitsFilters {
  status?: string;
  date?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  isTodayAndUpcoming?: boolean;
}

interface ContractorPanelVisitsFilterProps {
  filters: ContractorPanelVisitsFilters;
  onApply: (filters: ContractorPanelVisitsFilters) => void;
  onClear: () => void;
}

export const ContractorPanelVisitsFilter = ({
  filters,
  onApply,
  onClear,
}: ContractorPanelVisitsFilterProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [draftFilters, setDraftFilters] =
    useState<ContractorPanelVisitsFilters>(filters);

  const updateDraft = <K extends keyof ContractorPanelVisitsFilters>(
    key: K,
    value: ContractorPanelVisitsFilters[K],
  ) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    onClear();
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply(draftFilters);
    setIsOpen(false);
  };

  useEffect(() => {
    setDraftFilters(filters);
  }, [filters, isOpen]);

  const activeFilterCount = getActiveFilterCount(filters);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <RoundedIconButton
          variant="default"
          size="lg"
          floating
          badge={
            activeFilterCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-6 w-6 rounded-full p-0 text-xs flex items-center justify-center"
              >
                {activeFilterCount}
              </Badge>
            )
          }
        >
          <FilterIcon />
          <span className="sr-only">{t("contractorVisits.filter.title")}</span>
        </RoundedIconButton>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="flex flex-col h-[80vh] [&>button]:hidden rounded-t-xl"
      >
        <SheetHeader className="flex flex-row items-center justify-between p-4 pb-2">
          <SheetTitle className="text-lg">
            {t("contractorVisits.filter.title")}
          </SheetTitle>
          <RoundedIconButton
            variant="outline"
            size="md"
            onClick={() => setIsOpen(false)}
            className="border-2"
          >
            <X />
            <span className="sr-only">
              {t("contractorVisits.filter.close")}
            </span>
          </RoundedIconButton>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-6 mt-6 px-2 pb-36 overflow-y-auto">
          <DateFilter
            value={draftFilters.date}
            onChange={(value) =>
              setDraftFilters((prev) => applyDateChange(prev, value))
            }
          />

          <StatusFilter
            value={draftFilters.status}
            onChange={(value) => updateDraft("status", value)}
          />

          <SortByFilter
            value={draftFilters.sortBy}
            onChange={(value) => updateDraft("sortBy", value)}
          />

          <SortOrderFilter
            value={draftFilters.sortOrder}
            onChange={(value) => updateDraft("sortOrder", value)}
          />

          <TodayFilterPanelOption
            checked={draftFilters.isTodayAndUpcoming ?? false}
            onCheckedChange={(checked) =>
              setDraftFilters((prev) => applyTodayFilterChange(prev, checked))
            }
          />
        </div>

        <FilterActions
          activeFilterCount={activeFilterCount}
          onApply={handleApply}
          onClear={handleClear}
        />
      </SheetContent>
    </Sheet>
  );
};
